import { Badge, Box, Button, DialogBody, HStack, Image, Input, Separator, Spinner, Stack, Table, Text } from "@chakra-ui/react"
import { UI } from "components"
import { Mask } from "config"
import useMarketplaceOrders from "hooks/useMarketplaceOrders"
import { useState } from "react"
import Moment from "moment"

export default function MarketplaceOrders() {
    const [page, setPage]               = useState(1)
    const [search, setSearch]           = useState('')
    const [selectedOrder, setSelectedOrder] = useState(null)
    const { data, isLoading, isError }  = useMarketplaceOrders(page)

    const allOrders = data?.data ?? []
    const orders    = search
        ? allOrders.filter(o =>
            o.order_code.toLowerCase().includes(search.toLowerCase()) ||
            String(orderTotal(o)).includes(search)
          )
        : allOrders
    const total    = data?.total ?? 0
    const lastPage = data?.last_page ?? 1

    function orderTotal(order) {
        return Number(order.total) + Number(order.delivery?.shipping_amount ?? 0)
    }

    const pendingAmount = allOrders.reduce((acc, o) =>
        o.nucleus_receivable?.status === 'pending'
            ? acc + Number(o.nucleus_receivable.amount_to_nucleus)
            : acc
    , 0)

    const paidAmount = allOrders.reduce((acc, o) =>
        o.nucleus_receivable?.status === 'paid'
            ? acc + Number(o.nucleus_receivable.amount_to_nucleus)
            : acc
    , 0)

    const statusLabel = (status) => {
        const map = { pending: 'Pendente', paid: 'Pago', cancelled: 'Cancelado' }
        return map[status] ?? status
    }

    const statusColor = (status) => {
        const map = { pending: 'yellow', paid: 'green', cancelled: 'red' }
        return map[status] ?? 'gray'
    }

    return (
        <>
            <UI.Header title="Pedidos Marketplace" />

            {isError && (
                <Box bg="red.50" borderRadius={8} p={4} mb={4}>
                    <Text color="red.600" fontSize="sm">
                        Não foi possível carregar os pedidos marketplace. Verifique a integração.
                    </Text>
                </Box>
            )}

            <Stack gap={4}>
                {/* Summary cards */}
                {!isLoading && (
                    <HStack gap={4} flexWrap="wrap">
                        <Box flex={1} minW="180px" p={4} borderWidth={1} borderRadius={10} bg="bg.subtle">
                            <Text fontSize="xs" color="fg.muted" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
                                A receber
                            </Text>
                            <Text fontSize="2xl" fontWeight="bold" color="orange.600" mt={1}>
                                {Mask.price(Math.round(pendingAmount * 100))}
                            </Text>
                            {lastPage > 1 && (
                                <Text fontSize="2xs" color="fg.subtle" mt={1}>página atual</Text>
                            )}
                        </Box>
                        <Box flex={1} minW="180px" p={4} borderWidth={1} borderRadius={10} bg="bg.subtle">
                            <Text fontSize="xs" color="fg.muted" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
                                Já recebido
                            </Text>
                            <Text fontSize="2xl" fontWeight="bold" color="green.600" mt={1}>
                                {Mask.price(Math.round(paidAmount * 100))}
                            </Text>
                            {lastPage > 1 && (
                                <Text fontSize="2xs" color="fg.subtle" mt={1}>página atual</Text>
                            )}
                        </Box>
                        <Box flex={1} minW="180px" p={4} borderWidth={1} borderRadius={10} bg="bg.subtle">
                            <Text fontSize="xs" color="fg.muted" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
                                Total de pedidos
                            </Text>
                            <Text fontSize="2xl" fontWeight="bold" color="fg.default" mt={1}>
                                {total}
                            </Text>
                        </Box>
                    </HStack>
                )}

                <HStack justify="space-between">
                    <Text fontSize="sm" color="fg.muted">{total} pedido(s) encontrado(s)</Text>
                    <Input
                        placeholder="Buscar por código ou valor..."
                        size="sm"
                        maxW="260px"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </HStack>

                <Table.Root size="sm" variant="outline" interactive>
                    <Table.Header>
                        <Table.Row>
                            <Table.ColumnHeader>Pedido</Table.ColumnHeader>
                            <Table.ColumnHeader>Itens</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right">Total</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right">Repasse</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="center">Status repasse</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right">Data</Table.ColumnHeader>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {isLoading && (
                            <Table.Row>
                                <Table.Cell colSpan={6}>
                                    <HStack justify="center" py={6}><Spinner size="sm" /></HStack>
                                </Table.Cell>
                            </Table.Row>
                        )}

                        {!isLoading && orders.length === 0 && (
                            <Table.Row>
                                <Table.Cell colSpan={6}>
                                    <Text textAlign="center" color="fg.muted" py={6} fontSize="sm">
                                        Nenhum pedido marketplace encontrado.
                                    </Text>
                                </Table.Cell>
                            </Table.Row>
                        )}

                        {orders.map((order) => {
                            const receivable = order.nucleus_receivable
                            const itemCount  = order.items?.length ?? 0
                            return (
                                <Table.Row
                                    key={order.id}
                                    cursor="pointer"
                                    onClick={() => setSelectedOrder(order)}
                                    _hover={{ bg: 'bg.subtle' }}
                                >
                                    <Table.Cell>
                                        <Text fontFamily="mono" fontSize="xs">{order.order_code}</Text>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Text fontSize="sm">{itemCount} item(ns)</Text>
                                    </Table.Cell>
                                    <Table.Cell textAlign="right">
                                        <Stack gap={0} align="flex-end">
                                            <Text fontSize="sm" fontWeight="semibold">
                                                {Mask.price(orderTotal(order))}
                                            </Text>
                                            {Number(order.delivery?.shipping_amount ?? 0) > 0 && (
                                                <Text fontSize="2xs" color="fg.muted">
                                                    frete {Mask.price(order.delivery.shipping_amount)}
                                                </Text>
                                            )}
                                        </Stack>
                                    </Table.Cell>
                                    <Table.Cell textAlign="right">
                                        {receivable ? (
                                            <Stack gap={0} align="flex-end">
                                                <Text fontSize="sm" fontWeight="semibold" color="green.600">
                                                    {Mask.price(receivable.amount_to_nucleus)}
                                                </Text>
                                                <Text fontSize="2xs" color="fg.muted">
                                                    custo {Mask.price(receivable.cost_price)} + margem {Mask.price(receivable.margin)}
                                                </Text>
                                            </Stack>
                                        ) : (
                                            <Text fontSize="xs" color="fg.subtle">—</Text>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell textAlign="center">
                                        {receivable ? (
                                            <Badge colorPalette={statusColor(receivable.status)} size="sm">
                                                {statusLabel(receivable.status)}
                                            </Badge>
                                        ) : (
                                            <Text fontSize="xs" color="fg.subtle">—</Text>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell textAlign="right">
                                        <Text fontSize="xs" color="fg.muted">
                                            {order.paid_at ? Moment(order.paid_at).format('DD/MM/YY HH:mm') : '—'}
                                        </Text>
                                    </Table.Cell>
                                </Table.Row>
                            )
                        })}
                    </Table.Body>
                </Table.Root>

                {lastPage > 1 && (
                    <>
                        <Separator />
                        <HStack justify="center" gap={2}>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page <= 1}
                                onClick={() => setPage(p => p - 1)}
                            >
                                Anterior
                            </Button>
                            <Text fontSize="sm" color="fg.muted">{page} / {lastPage}</Text>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page >= lastPage}
                                onClick={() => setPage(p => p + 1)}
                            >
                                Próxima
                            </Button>
                        </HStack>
                    </>
                )}
            </Stack>

            {selectedOrder && (
                <UI.Dialog
                    open={!!selectedOrder}
                    onClose={() => setSelectedOrder(null)}
                    title={`Pedido ${selectedOrder.order_code}`}
                    size="lg"
                >
                    <DialogBody pb={6}>
                        <Stack gap={4}>
                            <Stack gap={2}>
                                <Text fontSize="sm" fontWeight="semibold" color="fg.muted">Produtos</Text>
                                {selectedOrder.items?.map((item) => (
                                    <HStack key={item.id} gap={3} p={3} borderWidth={1} borderRadius={8} bg="bg.subtle">
                                        {item.product?.image_path && (
                                            <Image
                                                src={item.product.image_path}
                                                alt={item.product.name}
                                                boxSize="48px"
                                                objectFit="cover"
                                                borderRadius={6}
                                                flexShrink={0}
                                            />
                                        )}
                                        <Box flex={1} minW={0}>
                                            <Text fontSize="sm" fontWeight="semibold" truncate>
                                                {item.product?.name ?? 'Produto'}
                                            </Text>
                                            <Text fontSize="xs" color="fg.muted">
                                                {item.quantity} × {Mask.price(item.price)}
                                            </Text>
                                        </Box>
                                        <Text fontSize="sm" fontWeight="bold" flexShrink={0}>
                                            {Mask.price(Number(item.price) * item.quantity)}
                                        </Text>
                                    </HStack>
                                ))}
                            </Stack>

                            <Separator />

                            <Stack gap={1}>
                                <HStack justify="space-between">
                                    <Text fontSize="sm" color="fg.muted">Produtos</Text>
                                    <Text fontSize="sm">{Mask.price(selectedOrder.total)}</Text>
                                </HStack>
                                {Number(selectedOrder.delivery?.shipping_amount ?? 0) > 0 && (
                                    <HStack justify="space-between">
                                        <Text fontSize="sm" color="fg.muted">Frete</Text>
                                        <Text fontSize="sm">{Mask.price(selectedOrder.delivery.shipping_amount)}</Text>
                                    </HStack>
                                )}
                                <HStack justify="space-between" pt={2} borderTopWidth={1} mt={1}>
                                    <Text fontSize="sm" fontWeight="bold">Total pago</Text>
                                    <Text fontSize="sm" fontWeight="bold">
                                        {Mask.price(orderTotal(selectedOrder))}
                                    </Text>
                                </HStack>
                            </Stack>

                            {selectedOrder.nucleus_receivable && (
                                <>
                                    <Separator />
                                    <Stack gap={1}>
                                        <Text fontSize="sm" fontWeight="semibold" color="fg.muted">Repasse ao núcleo</Text>
                                        <HStack justify="space-between">
                                            <Text fontSize="sm" color="fg.muted">Custo</Text>
                                            <Text fontSize="sm">{Mask.price(selectedOrder.nucleus_receivable.cost_price)}</Text>
                                        </HStack>
                                        <HStack justify="space-between">
                                            <Text fontSize="sm" color="fg.muted">Margem</Text>
                                            <Text fontSize="sm">{Mask.price(selectedOrder.nucleus_receivable.margin)}</Text>
                                        </HStack>
                                        <HStack justify="space-between" pt={2} borderTopWidth={1} mt={1}>
                                            <Text fontSize="sm" fontWeight="bold" color="green.600">Total repasse</Text>
                                            <Text fontSize="sm" fontWeight="bold" color="green.600">
                                                {Mask.price(selectedOrder.nucleus_receivable.amount_to_nucleus)}
                                            </Text>
                                        </HStack>
                                    </Stack>
                                </>
                            )}
                        </Stack>
                    </DialogBody>
                </UI.Dialog>
            )}
        </>
    )
}
